import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';

// DOC: https://docs.google.com/spreadsheets/d/1000000000000000000000000000000000000000/edit?gid=0#gid=0
interface AdLoadingMetrics {
  slotId: string;
  
  pageLoadStart: number;
  
  // js load
  gptScriptRequestStart?: number;
  gptLoadComplete?: number;

  adInitStart?: number;
  prebidStart?: number;
  prebidComplete?: number;
  consentStart?: number;
  consentComplete?: number;
  adRequestStart?: number;
  adRenderComplete?: number;
  adViewable?: number;
  
  isEmpty?: boolean;
  errorType?: string;
  // Cached GAM identifiers for click tracking per slot
  lineItemId?: string;
  creativeId?: string;
  isBackfill?: boolean;
}

declare global {
  interface Window {
    gumtreeDataLayer: any[];
    clientData?: any;
  }
}

interface GoogletagWithPubads {
  cmd?: Array<() => void>;
  pubads?: () => {
    addEventListener: (event: string, callback: (event: any) => void) => void;
    setTargeting: (key: string, value: string) => void;
    refresh: (slots: any[]) => void;
    getSlots?: () => any[];
  };
}

class AdPerformanceTracker {
    private metrics: Map<string, AdLoadingMetrics> = new Map();
    private targetSlots = ['tBanner', 'vipBanner'];
    private globalTimings: {
      gptScriptRequestStart?: number;
      gptLoadComplete?: number;
      prebidStart?: number;
      prebidComplete?: number;
      consentStart?: number;
      consentComplete?: number;
    } = {};
    
    constructor() {
      this.initGlobalTimings();
      this.setupGPTEventListeners();
    }
    
    private initGlobalTimings() {
      if (typeof window !== 'undefined' && window.performance) {
        const pageLoadStart = 0;
        this.targetSlots.forEach(slotId => {
          this.metrics.set(slotId, { 
            slotId,
            pageLoadStart,
          });
        });
      }
    }
    
    markGPTScriptRequestStart() {
      this.globalTimings.gptScriptRequestStart = performance.now();
    }
    setGPTScriptRequestStartTime(timestamp: number) {
      this.globalTimings.gptScriptRequestStart = timestamp;
    }
    
    initGPTLoadTracking() {
      if (typeof window === 'undefined') return;
      
      const googletag = (window as any).googletag;
      
      if (googletag && googletag.pubads) {
        this.markGPTLoaded();
      } else {
        (window as any).googletag = (window as any).googletag || {};
        (window as any).googletag.cmd = (window as any).googletag.cmd || [];
        (window as any).googletag.cmd.push(() => {
          this.markGPTLoaded();
        });
      }
    }
    
    private markGPTLoaded() {
      this.globalTimings.gptLoadComplete = performance.now();
    }
    
    markAdInitStart(slotId?: string) {
      const timestamp = performance.now();
      
      if (slotId && this.targetSlots.includes(slotId)) {
        const metric = this.metrics.get(slotId);
        if (metric) {
          metric.adInitStart = timestamp;
          this.applyGlobalTimingsToSlot(metric);
        }
      } else {
        this.targetSlots.forEach(slotId => {
          const metric = this.metrics.get(slotId);
          if (metric && !metric.adInitStart) {
            metric.adInitStart = timestamp;
            this.applyGlobalTimingsToSlot(metric);
          }
        });
      }
    }
    
    private applyGlobalTimingsToSlot(metric: AdLoadingMetrics) {
      if (this.globalTimings.gptScriptRequestStart && !metric.gptScriptRequestStart) {
        metric.gptScriptRequestStart = this.globalTimings.gptScriptRequestStart;
      }
      if (this.globalTimings.gptLoadComplete && !metric.gptLoadComplete) {
        metric.gptLoadComplete = this.globalTimings.gptLoadComplete;
      }
    }
    
    markPrebidStart() {
      this.globalTimings.prebidStart = performance.now();
    }
    
    markPrebidComplete() {
      this.globalTimings.prebidComplete = performance.now();
    }
    
    markConsentStart() {
      this.globalTimings.consentStart = performance.now();
    }
    
    markConsentComplete() {
      this.globalTimings.consentComplete = performance.now();
    }
    
    private markAdRequestStart(slotId: string) {
      if (!this.targetSlots.includes(slotId)) return;
      
      const metric = this.metrics.get(slotId);
      if (metric) {
        metric.adRequestStart = performance.now();
        
        if (this.globalTimings.prebidStart && !metric.prebidStart) {
          metric.prebidStart = this.globalTimings.prebidStart;
        }
        if (this.globalTimings.prebidComplete && !metric.prebidComplete) {
          metric.prebidComplete = this.globalTimings.prebidComplete;
        }
        if (this.globalTimings.consentStart && !metric.consentStart) {
          metric.consentStart = this.globalTimings.consentStart;
        }
        if (this.globalTimings.consentComplete && !metric.consentComplete) {
          metric.consentComplete = this.globalTimings.consentComplete;
        }
      }
    }
    
    private setupGPTEventListeners() {
      if (typeof window === 'undefined') return;
      
      const googletag = (window as any).googletag as GoogletagWithPubads;
      if (!googletag) return;
      
      googletag.cmd = googletag.cmd || [];
      
      googletag.cmd.push(() => {
        if (!googletag?.pubads) return;
        
        const pubads = googletag.pubads();
        
        pubads.addEventListener('slotRequested', (event: any) => {
          const slotId = event.slot.getSlotElementId();
          this.markAdRequestStart(slotId);
        });
        
        pubads.addEventListener('slotRenderEnded', (event: any) => {
          // Cache identifiers per-slot for later click tracking
          const slotId = event.slot.getSlotElementId();
          const existingMetric = this.metrics.get(slotId);
          // if (!this.targetSlots.includes(slotId)) return;
          const resp = event.slot.getResponseInformation?.() as any | undefined;

          const lineItemId = event.lineItemId ?? resp?.lineItemId ?? resp?.sourceAgnosticLineItemId ?? '';
          const creativeId = event.creativeId ?? resp?.creativeId ?? resp?.sourceAgnosticCreativeId ?? '';
          const isBackfill = resp?.isBackfill ?? event.isBackfill ?? false;

          // console.log('slotRenderEnded===!!!', event, resp);

          if (existingMetric) {
            existingMetric.lineItemId = lineItemId;
            existingMetric.creativeId = creativeId;
            existingMetric.isBackfill = isBackfill;
          } else {
            this.metrics.set(slotId, {
              slotId,
              pageLoadStart: 0,
              lineItemId: lineItemId,
              creativeId: creativeId,
              isBackfill: isBackfill,
            });
          }
          this.reportAdRes({
            slotId,
            isEmpty: event.isEmpty,
            creativeId,
            lineItemId,
            isBackfill,
          });

          if (!this.targetSlots.includes(slotId)) return;
          
          const metric = this.metrics.get(slotId);
          if (metric) {
            metric.adRenderComplete = performance.now();
            metric.isEmpty = event.isEmpty;
            
            this.reportToGA4(metric);
          }
        });
        
        pubads.addEventListener('impressionViewable', (event: any) => {
          const slotId = event.slot.getSlotElementId();
          if (!this.targetSlots.includes(slotId)) return;
          
          const metric = this.metrics.get(slotId);
          if (metric) {
            metric.adViewable = performance.now();
            this.updateGA4Metrics(slotId, { adViewable: metric.adViewable });
          }
        });
      });
    }
    
    private reportToGA4(metrics: AdLoadingMetrics) {
      // console.log('reportToGA4===', metrics)
      if (typeof window === 'undefined') return;
      
      const totalTime = metrics.adRenderComplete && metrics.pageLoadStart !== undefined
        ? metrics.adRenderComplete - metrics.pageLoadStart : 0;
      const adLoadTime = metrics.adRenderComplete && metrics.adRequestStart
        ? metrics.adRenderComplete - metrics.adRequestStart : 0;
      const initTime = metrics.adRequestStart && metrics.adInitStart
        ? metrics.adRequestStart - metrics.adInitStart : 0;
      const prebidTime = metrics.prebidComplete && metrics.prebidStart 
        ? metrics.prebidComplete - metrics.prebidStart : 0;
      const consentTime = metrics.consentComplete && metrics.consentStart
        ? metrics.consentComplete - metrics.consentStart : 0;
      
      
      // js load time
      const gptTotalTime = metrics.gptLoadComplete && metrics.pageLoadStart !== undefined
        ? metrics.gptLoadComplete - metrics.pageLoadStart : 0;
      const jsTagLoadTime = metrics.gptScriptRequestStart && metrics.pageLoadStart !== undefined
        ? metrics.gptScriptRequestStart - metrics.pageLoadStart : 0;
      const jsTagLoad2entryInit = metrics.gptScriptRequestStart && metrics.gptLoadComplete !== undefined
        ? metrics.gptLoadComplete - metrics.gptScriptRequestStart : 0;
      
      const performanceTrackData = {
        event: 'ad_loading_performance',
        slot_id: metrics.slotId,
        total_time: Math.round(totalTime),
        ad_load_time: Math.round(adLoadTime),
        init_time: Math.round(initTime),
        prebid_time: Math.round(prebidTime),
        consent_time: Math.round(consentTime),
        
        // GPT js
        gpt_load_time: Math.round(gptTotalTime),
        js_tag_load_time: Math.round(jsTagLoadTime),
        js_tag_load2entry_init: Math.round(jsTagLoad2entryInit),
        
        // load result
        is_empty: metrics.isEmpty || false
      }
      // console.log('metrics===', metrics)
      // console.log('gptTotalTime===', performanceTrackData)
      trackGA4Event(performanceTrackData);
    }

    private reportAdRes(metrics) {
      // console.log('reportAdRes===', metrics.slotId, metrics)  
      if (typeof window === 'undefined') return;
      const trackDataBI = {
        event: metrics.isEmpty ? 'gam_ads_view_error' : 'gam_ads_view_success',
        lineItemId: metrics.lineItemId || '',
        creativeId: metrics.creativeId,
        isBackfill: metrics.isBackfill,
        slot_name: metrics.slotId,
      }
      trackGA4Event(trackDataBI);
    }
    
    private updateGA4Metrics(slotId: string, updates: Partial<AdLoadingMetrics>) {
      const metric = this.metrics.get(slotId);
      if (metric && updates.adViewable) {
        // unnecessary ad_viewable
      }
    }
    
    getMetrics(slotId?: string) {
      if (slotId) {
        return this.metrics.get(slotId);
      }
      return Object.fromEntries(this.metrics);
    }
    
    public trackAdClick(slotId: string) {
      const metrics = this.metrics.get(slotId);
      
      if (!metrics) return;
  
      const payload = {
        event: 'gam_ads_click',
        slot_name: slotId,
        lineItemId: metrics.lineItemId || '',
        creativeId: metrics.creativeId,
        isBackfill: metrics.isBackfill,
      } as const;
  
      trackGA4Event(payload);
    }
  }

  
  export const adPerformanceTracker = new AdPerformanceTracker();
